/**
 * Canonical Provider Option selection contract for Canvas AI nodes.
 *
 * DEFAULT  — node follows capability Admin `is_default` (no permanent pin).
 * EXPLICIT — user picked a stable provider_option_id; survives Admin default changes.
 *
 * Legacy nodes without `providerSelectionMode` are treated as DEFAULT so
 * auto-written historical `providerOptionId` snapshots do not permanently
 * pin every node to an old default (e.g. Vistoera 换装).
 */

import type { WorkspaceProviderOption } from '../../../api'

export type ProviderSelectionMode = 'DEFAULT' | 'EXPLICIT'

export const PROVIDER_SELECTION_MODE_DEFAULT: ProviderSelectionMode = 'DEFAULT'
export const PROVIDER_SELECTION_MODE_EXPLICIT: ProviderSelectionMode = 'EXPLICIT'

/** Normalize persisted / missing mode. Missing → DEFAULT (legacy-safe). */
export function normalizeProviderSelectionMode(raw: unknown): ProviderSelectionMode {
  const v = typeof raw === 'string' ? raw.trim().toUpperCase() : ''
  if (v === PROVIDER_SELECTION_MODE_EXPLICIT) return PROVIDER_SELECTION_MODE_EXPLICIT
  return PROVIDER_SELECTION_MODE_DEFAULT
}

/** Backend already returns canonical order; keep stable if already sorted. */
export function sortProviderOptionsCanonical(options: WorkspaceProviderOption[]): WorkspaceProviderOption[] {
  return [...options].sort((a, b) => {
    const aDefault = a.is_default ? 0 : 1
    const bDefault = b.is_default ? 0 : 1
    if (aDefault !== bDefault) return aDefault - bDefault
    const aAvail = String(a.availability || '').toUpperCase() === 'AVAILABLE' ? 0 : 1
    const bAvail = String(b.availability || '').toUpperCase() === 'AVAILABLE' ? 0 : 1
    if (aAvail !== bAvail) return aAvail - bAvail
    return 0
  })
}

export function findFirstSelectableProviderOption(
  options: WorkspaceProviderOption[],
): WorkspaceProviderOption | null {
  return options.find(isProviderOptionSelectable) ?? null
}

/** Rebind EXPLICIT selection when persisted option id is from another environment. */
export function findProviderOptionByDisplayName(
  options: WorkspaceProviderOption[],
  displayName: string | null | undefined,
): WorkspaceProviderOption | null {
  const target = typeof displayName === 'string' ? displayName.trim().toLowerCase() : ''
  if (!target) return null
  return (
    options.find(
      (o) =>
        isProviderOptionSelectable(o) &&
        String(o.frontend_display_name || '').trim().toLowerCase() === target,
    ) ?? null
  )
}

export function findCanonicalDefaultOption(
  options: WorkspaceProviderOption[],
  serverDefaultId?: string | null,
): WorkspaceProviderOption | null {
  if (!options.length) return null
  const sid = typeof serverDefaultId === 'string' ? serverDefaultId.trim() : ''
  if (sid) {
    const fromServer = options.find((o) => o.provider_option_id === sid)
    if (fromServer && isProviderOptionSelectable(fromServer)) return fromServer
  }
  const flaggedDefault = options.find((o) => o.is_default)
  if (flaggedDefault && isProviderOptionSelectable(flaggedDefault)) return flaggedDefault
  return findFirstSelectableProviderOption(options) ?? options[0] ?? null
}

/**
 * Resolve effective provider_option_id for UI + submit.
 * DEFAULT ignores stored id (follows Admin default).
 * EXPLICIT keeps the stored id even when unavailable/missing so we never
 * silently switch — the node UI surfaces an unavailable state instead.
 */
export function resolveCapabilityProviderOptionId(args: {
  selectionMode: ProviderSelectionMode | string | null | undefined
  storedProviderOptionId: string | null | undefined
  /** Persisted UI label — used to rebind when option UUIDs differ per environment. */
  storedProviderDisplayName?: string | null
  options: WorkspaceProviderOption[]
  serverDefaultProviderOptionId?: string | null
}): string | null {
  const options = args.options
  const mode = normalizeProviderSelectionMode(args.selectionMode)
  if (mode === PROVIDER_SELECTION_MODE_EXPLICIT) {
    const storedId = typeof args.storedProviderOptionId === 'string' ? args.storedProviderOptionId.trim() : ''
    if (storedId) {
      if (options.some((o) => o.provider_option_id === storedId)) return storedId
      const rebound = findProviderOptionByDisplayName(options, args.storedProviderDisplayName)
      if (rebound) return rebound.provider_option_id
      return storedId
    }
  }
  if (!options.length) return null
  const def = findCanonicalDefaultOption(options, args.serverDefaultProviderOptionId)
  return def?.provider_option_id ?? null
}

/** True when EXPLICIT selection is missing from catalog or not AVAILABLE. */
export function isExplicitProviderSelectionInvalid(args: {
  selectionMode: ProviderSelectionMode | string | null | undefined
  storedProviderOptionId: string | null | undefined
  storedProviderDisplayName?: string | null
  options: WorkspaceProviderOption[]
}): boolean {
  if (normalizeProviderSelectionMode(args.selectionMode) !== PROVIDER_SELECTION_MODE_EXPLICIT) return false
  const storedId = typeof args.storedProviderOptionId === 'string' ? args.storedProviderOptionId.trim() : ''
  if (!storedId) return false
  const resolvedId = resolveCapabilityProviderOptionId({
    selectionMode: args.selectionMode,
    storedProviderOptionId: args.storedProviderOptionId,
    storedProviderDisplayName: args.storedProviderDisplayName,
    options: args.options,
  })
  const hit = findProviderOptionById(args.options, resolvedId)
  if (!hit) return true
  return String(hit.availability || '').toUpperCase() !== 'AVAILABLE'
}

export function findProviderOptionById(
  options: WorkspaceProviderOption[],
  providerOptionId: string | null | undefined,
): WorkspaceProviderOption | null {
  if (!providerOptionId) return null
  return options.find((o) => o.provider_option_id === providerOptionId) ?? null
}

export function isProviderOptionSelectable(option: WorkspaceProviderOption): boolean {
  return String(option.availability || '').toUpperCase() === 'AVAILABLE'
}

/** Fields patch when the user explicitly picks from the Provider Selector. */
export function buildExplicitProviderSelectionPatch(
  providerOptionId: string,
  providerDisplayName?: string | null,
): {
  providerSelectionMode: ProviderSelectionMode
  providerOptionId: string
  providerDisplayName?: string | null
} {
  const name = typeof providerDisplayName === 'string' ? providerDisplayName.trim() : ''
  return {
    providerSelectionMode: PROVIDER_SELECTION_MODE_EXPLICIT,
    providerOptionId,
    ...(name ? { providerDisplayName: name } : {}),
  }
}

/** After a DEFAULT-mode generation: keep following Admin default (no pin). */
export function buildDefaultProviderSelectionPersistPatch(): {
  providerSelectionMode: ProviderSelectionMode
  providerOptionId: null
} {
  return {
    providerSelectionMode: PROVIDER_SELECTION_MODE_DEFAULT,
    providerOptionId: null,
  }
}

/** New Provider-Option-capable node defaults. */
export function buildProviderOptionNodeDefaultFields(): {
  providerSelectionMode: ProviderSelectionMode
  providerOptionId: null
} {
  return {
    providerSelectionMode: PROVIDER_SELECTION_MODE_DEFAULT,
    providerOptionId: null,
  }
}
